import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAircraft,
  fetchAircraftByUser,
  fetchAircraftById,
  fetchCreateData,
  createAircraft,
  updateAircraft,
  deleteAircraft,
  permanentDeleteAircraft,
  restoreAircraft,
  deleteAircraftImage,
  deleteSeatingLayoutImage,
  fetchAircraftRoutes,
  deleteAircraftRoute,
  checkAircraftRegistration,
  checkRouteExists,
  fetchAircraftModels,
  exportAircraft,
  fetchAircraftImages,
  fetchSeatingLayout,
  fetchAircraftBookings,
  createAircraftRoute,
  updateAircraftRoute,
  deleteAircraftRouteById,
  fetchRouteBookings,
  clearError,
  clearCurrentAircraft
} from '../../store/slices/aircraftSlice';

export const useAircraftManagement = () => {
  const dispatch = useDispatch();
  const {
    aircraft,
    currentAircraft,
    routes,
    aircraftModels,
    createData,
    images,
    seatingLayout,
    bookings,
    routeBookings,
    loading,
    error,
    pagination
  } = useSelector((state) => state.aircraft);

  // Fetch all aircraft
  const loadAircraft = useCallback((params) => {
    // For aircraft management, we should load aircraft by user ID
    // This matches the PHP behavior of /aircraft_details/{user_id}
    if (params?.userId) {
      return dispatch(fetchAircraftByUser(params));
    } else {
      // Fallback to general aircraft fetch if no userId provided
      return dispatch(fetchAircraft(params));
    }
  }, [dispatch]);

  // Fetch aircraft by user
  const loadAircraftByUser = useCallback((userId) => {
    return dispatch(fetchAircraftByUser(userId));
  }, [dispatch]);

  // Fetch single aircraft
  const loadAircraftById = useCallback((id) => {
    return dispatch(fetchAircraftById(id));
  }, [dispatch]);

  // Fetch create form data
  const loadCreateData = useCallback((userId) => {
    return dispatch(fetchCreateData(userId));
  }, [dispatch]);

  // Create new aircraft
  const addAircraft = useCallback((data) => {
    return dispatch(createAircraft(data));
  }, [dispatch]);

  // Update aircraft
  const editAircraft = useCallback((id, data) => {
    return dispatch(updateAircraft({ id, data }));
  }, [dispatch]);

  // Delete aircraft (soft delete)
  const removeAircraft = useCallback((id) => {
    return dispatch(deleteAircraft(id));
  }, [dispatch]);

  // Permanent delete aircraft
  const permanentRemoveAircraft = useCallback((id) => {
    return dispatch(permanentDeleteAircraft(id));
  }, [dispatch]);

  // Restore aircraft
  const restoreDeletedAircraft = useCallback((id) => {
    return dispatch(restoreAircraft(id));
  }, [dispatch]);

  // Delete image
  const removeImage = useCallback((imageData) => {
    return dispatch(deleteAircraftImage(imageData));
  }, [dispatch]);

  // Delete seating layout image
  const removeSeatingLayoutImage = useCallback((id) => {
    return dispatch(deleteSeatingLayoutImage(id));
  }, [dispatch]);

  // Routes
  const loadRoutes = useCallback((id) => {
    return dispatch(fetchAircraftRoutes(id));
  }, [dispatch]);

  const removeRoute = useCallback((routeData) => {
    return dispatch(deleteAircraftRoute(routeData));
  }, [dispatch]);

  const addRoute = useCallback((id, data) => {
    return dispatch(createAircraftRoute({ id, data }));
  }, [dispatch]);

  const editRoute = useCallback((routeId, data) => {
    return dispatch(updateAircraftRoute({ routeId, data }));
  }, [dispatch]);

  const removeRouteById = useCallback((routeId) => {
    return dispatch(deleteAircraftRouteById(routeId));
  }, [dispatch]);

  // Images
  const loadImages = useCallback((id) => {
    return dispatch(fetchAircraftImages(id));
  }, [dispatch]);

  // Seating layout
  const loadSeatingLayout = useCallback((id) => {
    return dispatch(fetchSeatingLayout(id));
  }, [dispatch]);

  // Bookings
  const loadBookings = useCallback((id) => {
    return dispatch(fetchAircraftBookings(id));
  }, [dispatch]);

  const loadRouteBookings = useCallback((routeId) => {
    return dispatch(fetchRouteBookings(routeId));
  }, [dispatch]);

  // Check registration
  const checkRegistration = useCallback((registrationNumber) => {
    return dispatch(checkAircraftRegistration(registrationNumber));
  }, [dispatch]);

  // Check route exists
  const checkRoute = useCallback((params) => {
    return dispatch(checkRouteExists(params));
  }, [dispatch]);

  // Aircraft models
  const loadAircraftModels = useCallback((search) => {
    return dispatch(fetchAircraftModels(search));
  }, [dispatch]);

  // Export
  const exportData = useCallback((format) => {
    return dispatch(exportAircraft(format));
  }, [dispatch]);

  // Clear functions
  const clearAircraftError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const clearCurrent = useCallback(() => {
    dispatch(clearCurrentAircraft());
  }, [dispatch]);

  return {
    // State
    aircraft,
    currentAircraft,
    routes,
    aircraftModels,
    createData,
    images,
    seatingLayout,
    bookings,
    routeBookings,
    loading,
    error,
    pagination,
    
    // Actions
    loadAircraft,
    loadAircraftByUser,
    loadAircraftById,
    loadCreateData,
    addAircraft,
    editAircraft,
    removeAircraft,
    permanentRemoveAircraft,
    restoreDeletedAircraft,
    removeImage,
    removeSeatingLayoutImage,
    loadRoutes,
    removeRoute,
    addRoute,
    editRoute,
    removeRouteById,
    loadImages,
    loadSeatingLayout,
    loadBookings,
    loadRouteBookings,
    checkRegistration,
    checkRoute,
    loadAircraftModels,
    exportData,
    clearAircraftError,
    clearCurrent
  };
};

export default useAircraftManagement;
